import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Popup, Icon, Form, Input, Header, Dropdown } from 'semantic-ui-react';
import cn from 'classnames';

import { CacheInputs, Planning, Portfolio, Accounts } from 'actions';
import { colors } from 'utils/colors';
import { convertToTickersAndWeights } from 'utils/helpers';
import {
  Message,
  ErrorLabel,
  Button,
  Segment,
  PortfolioAccountSelector,
} from 'components';
import Result from './Result';

@connect((state) => ({
  isFetching: state.Planning.isFetching,
  longevity: state.Planning.longevity,
  portfolios: state.Portfolio.model.concat(state.Portfolio.user),
  portLongevityInputs: state.CacheInputs.planning.portfolio_longevity,
  accountList: state.Accounts.accountList,
  accountData: state.Accounts.accountData,
}))
export default class RuinProbability extends Component {
  componentWillMount() {
    const { dispatch, portfolios } = this.props;

    dispatch(Accounts.fetchAll());
    dispatch(Accounts.fetchAllData());

    if (!portfolios.length) {
      dispatch(Portfolio.fetchUser());
      dispatch(Portfolio.fetchModel());
    }

    return false;
  }

  getNewPortDate = (port) => {
    if (port && !port.init_date) {
      return false;
    }

    if (!port || !port.init_date) {
      return false;
    }
    const date = new Date(port.init_date);
    const today = new Date();
    const monthDifference =
      today.getMonth() -
      date.getMonth() +
      12 * (today.getFullYear() - date.getFullYear());
    if (monthDifference > 60) {
      return false;
    }

    const init = date.toDateString().toString().split(/\s+/);

    const months = {
      Jan: 'January',
      Feb: 'February',
      Mar: 'March',
      Apr: 'April',
      May: 'May',
      Jun: 'June',
      Jul: 'July',
      Aug: 'August',
      Sept: 'September',
      Oct: 'October',
      Nov: 'November',
      Dec: 'December',
    };

    return `${months[init[1]]} ${init[3]}`;
  };

  getParamsPort = () => {
    const { portfolios, portLongevityInputs } = this.props;
    const { portID } = portLongevityInputs;
    const portfolio = portfolios.find(
      (item) => Number(item.id) === Number(portID)
    );

    return portfolio
      ? {
          symbols: portfolio.symbols,
          weights: portfolio.weights,
          region: portfolio.region,
          gic_info: portfolio.gic_info,
          init_date: portfolio.init_date,
        }
      : {};
  };

  getParamsAcc = () => {
    const { accountList, portLongevityInputs } = this.props;
    const { portID } = portLongevityInputs;
    const account = (accountList || []).find(
      (item) => Number(item.id) === Number(portID)
    );

    if (!account) return {};

    const accToPort = convertToTickersAndWeights(account.holdings);
    const gicFormat = /^GIC\d*:MKT$/;
    const gic_info = account.holdings
      .filter(
        (holding) =>
          gicFormat.test(holding.security) ||
          gicFormat.test(
            holding.security_detail ? holding.security_detail.ticker : ''
          )
      )
      .reduce((result, holding) => {
        result[holding.ticker] = {
          gic_name: holding.gic_name,
          gic_quantity: holding.quantity,
          gic_rate: holding.gic_rate,
          gic_ticker: holding.gic_ticker,
        };
        return result;
      }, {});

    return {
      symbols: accToPort.tickers,
      weights: accToPort.weights,
      region: 'CA',
      gic_info,
      init_date: account.analytics_start_date,
    };
  };

  handleKeyPress = (e) => e.key === '.' && e.preventDefault();

  handlePortfolioChange = (e, { value }) =>
    this.props.dispatch(
      CacheInputs.updateField('planning', 'portfolio_longevity', {
        portfolio: value,
        portID: value,
        errors: { portfolio: null },
      })
    );

  handleHeaderChange = (value) => {
    const { dispatch, portLongevityInputs } = this.props;
    const { errors } = portLongevityInputs;

    dispatch(Planning.clearSectionData('longevity'));

    return dispatch(
      CacheInputs.updateField('planning', 'portfolio_longevity', {
        isPorts: value,
        portID: '',
        portfolio: null,
        errors: { ...errors, portID: null, portfolio: null },
      })
    );
  };

  handleChange = (e, { name, value }) => {
    const { dispatch } = this.props;

    if (name === 'gender') {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          gender: value,
          errors: { gender: null },
        })
      );
    }

    if (
      (name === 'retAge' || name === 'annuityRate' || name === 'lifeExp') &&
      Number(value) > 100
    ) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          [name]: 100,
          errors: { [name]: null },
        })
      );
    }

    return (
      value.length < 10 &&
      dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          [name]: value,
          errors: { [name]: null },
        })
      )
    );
  };

  scrollHandler = (element, to, duration) => {
    if (duration <= 0) return false;
    const difference = to - element.scrollTop;
    const perTick = (difference / duration) * 10;

    return setTimeout(() => {
      element.scrollTop += perTick;
      if (element.scrollTop === to) return false;
      return this.scrollHandler(element, to, duration - 10);
    }, 10);
  };

  hasEmptyValue = (values) =>
    values.some((field) => {
      const { dispatch, portLongevityInputs } = this.props;

      if (!portLongevityInputs[field]) {
        return dispatch(
          CacheInputs.updateField('planning', 'portfolio_longevity', {
            errors: { [field]: 'This field is required' },
          })
        );
      }

      return false;
    });

  handleSubmit = () => {
    const { dispatch, portLongevityInputs } = this.props;
    const {
      useDefault,
      lifeExp,
      annuityRate,
      retAge,
      init,
      gender,
      cf,
      riskFree,
      portID,
      isPorts,
    } = portLongevityInputs;
    const params = {
      retAge,
      init,
      gender,
      cf,
      init_date: null,
      region: 'CA',
      targetProb: 0.05,
    };

    if (this.hasEmptyValue(Object.keys({ retAge, init, gender, cf }))) {
      return false;
    }

    if (!useDefault && !lifeExp) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          errors: { lifeExp: 'This field is required' },
        })
      );
    }

    if (riskFree && !annuityRate) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          errors: { annuityRate: 'This field is required' },
        })
      );
    }

    if (!riskFree && !portID) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          errors: { portfolio: 'This field is required' },
        })
      );
    }

    if (annuityRate) {
      params.annuityRate = annuityRate / 100;
    }

    if (lifeExp) {
      params.lifeExp = lifeExp;
    }

    if (portID) {
      const selectionParams = isPorts
        ? this.getParamsPort()
        : this.getParamsAcc();
      params.symbols = selectionParams.symbols;
      params.weights = selectionParams.weights;
      params.gic_info = selectionParams.gic_info;
      params.init_date = selectionParams.init_date;
    }

    return dispatch(Planning.analyzeLongevity(params)).then((action) => {
      const element = document.documentElement;
      const target = document.getElementById('longevity-ref').offsetHeight;

      return (
        action && !!action.result && this.scrollHandler(element, target, 300)
      );
    });
  };

  render() {
    const {
      isFetching,
      longevity,
      portfolios,
      portLongevityInputs,
      history,
      user_region,
      accountData,
    } = this.props;
    const {
      retAge,
      init,
      cf,
      gender,
      riskFree,
      annuityRate,
      errors,
      portID,
      isPorts,
    } = portLongevityInputs;
    const curPort = portfolios.find((port) => Number(port.id) === portID);

    const portDate =
      portID &&
      curPort &&
      (isPorts
        ? this.getNewPortDate(curPort.init_date)
        : accountData
        ? this.getNewPortDate(
            accountData.find((port) => Number(port.id) === portID)
              .analytics_start_date
          )
        : false);

    let textValue = '';

    if (portID && portID !== 0) {
      if (isPorts) {
        if (portfolios && portfolios.length > 0) {
          const match = curPort;
          if (match) {
            textValue = match.name || '';
          }
        }
      } else if (accountData && accountData.length > 0) {
        const match = accountData.find((p) => Number(p.id) === Number(portID));
        if (match) {
          textValue = match.label || '';
        }
      }
    }

    return (
      <Segment>
        <Header
          size="large"
          content="Probability of Ruin"
          textAlign="center"
          subheader={
            <div className="sub header">
              What are the chances that you will outlive your money? <br /> See
              the full picture based on 10,000 simulations of conditional
              mortality and historical portfolio returns.
            </div>
          }
        />

        <StyledForm
          onSubmit={this.handleSubmit}
          style={{
            width: '80%',
            maxWidth: '800px',
            margin: '2rem auto 0 auto',
          }}
        >
          <Form.Group widths="equal">
            <Form.Field style={{ position: 'relative' }}>
              <label>Age:</label>
              <Input
                name="retAge"
                type="number"
                fluid
                placeholder="years"
                value={retAge || ''}
                onChange={this.handleChange}
                onKeyPress={this.handleKeyPress}
              />
              <ErrorLabel className="top" msg={errors.retAge} />
            </Form.Field>

            <Form.Field style={{ position: 'relative' }}>
              <label>
                Sex:{' '}
                <Popup
                  trigger={<Icon name="question circle outline" />}
                  position="right center"
                  style={{ height: 'initial' }}
                >
                  {user_region === 'CA'
                    ? 'We are using mortality rates from Statistics Canada in the calculations and the data is available for males and females only.'
                    : 'We are using mortality rates from the National Center for Health Statistics in the calculations and the data is available for males and females only.'}
                </Popup>
              </label>
              <Dropdown
                name="gender"
                compact
                placeholder="Sex"
                selection
                options={[
                  { key: 'm', value: 'M', text: 'Male' },
                  { key: 'f', value: 'F', text: 'Female' },
                ]}
                value={gender}
                onChange={this.handleChange}
              />
              <ErrorLabel className="top" msg={errors.gender} />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field style={{ position: 'relative' }}>
              <label>Savings:</label>
              <Input
                name="init"
                type="number"
                fluid
                value={init || ''}
                icon="dollar"
                iconPosition="left"
                onChange={this.handleChange}
                onKeyPress={this.handleKeyPress}
              />
              <ErrorLabel className="top" msg={errors.init} />
              <SubLabel>How much you currently have.</SubLabel>
            </Form.Field>

            <Form.Field style={{ position: 'relative' }}>
              <label>Withdrawal per year:</label>
              <Input
                name="cf"
                type="number"
                fluid
                value={cf || ''}
                icon="dollar"
                iconPosition="left"
                onChange={this.handleChange}
                onKeyPress={this.handleKeyPress}
              />
              <ErrorLabel className="top" msg={errors.cf} />
              <SubLabel>
                How much you withdraw from your savings.
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{ fontSize: '13px', verticalAlign: 'initial' }}
                    />
                  }
                  position="right center"
                  content="This amount is what you spend per year over and above any retirement income you may receive, i.e., spending that has to be financed by your savings. We assume that this amount grows with inflation at 2% over time."
                />
              </SubLabel>
            </Form.Field>
          </Form.Group>

          <Form.Field
            style={{
              position: 'relative',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <label>
              Choose the portfolio for your savings:
              <Popup
                trigger={
                  <Icon
                    name="question circle outline"
                    style={{ fontSize: '16px', verticalAlign: 'initial' }}
                  />
                }
                position="right center"
                wide
                content={`You can choose a${
                  user_region === 'US' ? ' ' : ' WS '
                }model portfolio or any of your saved portfolios or accounts here. See the “Portfolios” segment.`}
              />
            </label>
            <PortfolioAccountSelector
              value={`${portID}`}
              text={textValue}
              onChange={this.handlePortfolioChange}
              isPorts={isPorts}
              updateIsPorts={this.handleHeaderChange}
            />
          </Form.Field>
          <Message
            icon
            visible={!!portDate}
            warning
            style={{
              margin: '1rem auto 0 auto',
              maxWidth: '45rem',
              textAlign: 'left',
            }}
          >
            <Icon name="exclamation" />
            <Message.Content>
              This portfolio has a start date of <strong>{portDate}</strong>.
              You may wish to use a portfolio with a longer history. You can use
              the ETF Portfolio Builder in the "Portfolios" segment. Once saved,
              the portfolio will appear in the dropdown list here.
            </Message.Content>
          </Message>

          {riskFree && (
            <Form.Field
              style={{ position: 'relative', width: '10rem', margin: '0 auto' }}
            >
              <label>Enter the annual return:</label>
              <Input
                type="number"
                step="0.01"
                name="annuityRate"
                icon="percent"
                value={annuityRate || ''}
                onChange={this.handleChange}
              />
              <ErrorLabel msg={errors.annuityRate} />
            </Form.Field>
          )}
          <div style={{ margin: '2rem' }} />

          <Button
            className={cn('centered right', { loading: isFetching })}
            color={colors.teal}
            icon="calculator"
          >
            Calculate
          </Button>
        </StyledForm>

        <Result isFetching={isFetching} result={longevity} history={history} />
      </Segment>
    );
  }
}

RuinProbability.propTypes = {
  dispatch: PropTypes.func,
  isFetching: PropTypes.bool,
  portfolios: PropTypes.array,
  longevity: PropTypes.object,
  portLongevityInputs: PropTypes.object,
  accountList: PropTypes.array,
  accountData: PropTypes.array,
};

RuinProbability.defaultProps = {
  dispatch: () => false,
  portfolios: [],
  longevity: null,
  isFetching: false,
  portLongevityInputs: {},
  accountList: [],
  accountData: [],
};

const SubLabel = styled.div`
  color: rgba(0, 0, 0, 0.5);
  line-height: 0.8rem;
  font-style: italic;
  font-weight: 100;
  font-size: 16px;
  margin-top: 1rem;
`;

const StyledForm = styled(Form)`
  &&& .field > label {
    font-size: 16px;
  }
`;
